import { useLayoutEffect, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "dayjs";
import {
  CallState,
  CorePackagesCheckout,
  CorePackagesCheckoutV2,
  IdEnum,
  IResult,
  ITripTerminus,
  Lodging,
  LodgingSelectionEnum,
  PackagedLodging,
  PackagedOutboundFareSlice,
  PackagedRoom,
  PackageFetchDetailsEnum,
  PackageShopEnum,
  SafePackageDetails,
} from "redmond";
import {
  setOrigin,
  setDestination,
  setTravelers,
  setDepartureDate,
  setReturnDate,
} from "../modules/search/actions/actions";
import {
  setSearchedDates,
  setSearchedOriginResult,
  setSearchedDestinationResult,
  setSearchedTravelers,
} from "../modules/availability/actions/actions";
import {
  setDateRange,
  selectLodging,
  selectPackageByLodgingId,
  selectRoomType,
  setPackageHotelShopResults,
} from "../modules/hotel-shop/actions/actions";
import { PackageHotelShopCallState } from "../modules/hotel-shop/reducer/state";
import {
  setPackagesFlightShopAirportsV2,
  setPackagesFlightShopResults,
  setPackagesTripDetails,
  setPackagesChosenOutgoingSlice,
  setPackagesChosenReturnSlice,
} from "../modules/flight-shop/actions/actions";

/**
 * Seeds the legacy Redux store in capone-travel-portal with data from b2bportal's
 * state for Packages. This is necessary for the checkout flow (e.g DesktopPackagesBookWorkflow)
 * to function, as it reads search, hotel, and flight data from Redux via selectors.
 *
 * Under v2 (`coreV2`), the handoff carries no package data — only navigation
 * callbacks and the flight airports reference map — so this only seeds
 * `airportsV2`. Everything else (search/hotel/flight selection, pricing) is
 * read from the GetPackage snapshot via the package-identity reducer instead.
 */
export const useCorePackagesSync = (
  core?: CorePackagesCheckout,
  coreV2?: CorePackagesCheckoutV2,
): boolean => {
  const dispatch = useDispatch();

  // Flag to track whether this hook has finished syncing or not.
  const [synced, setSynced] = useState(false);

  // We need to use `useLayoutEffect` here in order to make things synchronous
  // and avoid flickers/effects running pre-maturely.
  useLayoutEffect(() => {
    if (coreV2 != null) {
      dispatch(setPackagesFlightShopAirportsV2(coreV2.flightAirports));
      setSynced(true);
      return;
    }

    if (core == null) return;

    // Extract Data
    const {
      search: { origin, destination, departureDate, returnDate, passengers },
      hotels: {
        selectedLodging,
        selectedRoom,
        data: { hotelShopData },
      },
      flights: {
        selectedOutboundFlight,
        selectedReturnFlight,
        data: {
          flightShopData,
          outboundTripDetailsData,
          returnTripDetailsData,
        },
      },
    } = core;

    const tripTerminus = {
      id: { Id: IdEnum.Flight, code: origin.airportRegion },
      label: origin.label,
    } as ITripTerminus;

    const destinationResult: IResult = {
      id: {
        Id: IdEnum.Lodgings,
        lodgingSelection: {
          LodgingSelection: LodgingSelectionEnum.Place,
          searchTerm: destination.searchTerm,
          placeId: destination.placeId,
          placeTypes: destination.placeTypes,
        },
      },
      label: destination.label,
    };

    const fromDate = dayjs(departureDate).toDate();
    const untilDate = dayjs(returnDate).toDate();

    const infants = [
      ...Array.from({ length: passengers.infantsInSeatCount }, () => ({
        age: 0,
        inSeat: true,
      })),
      ...Array.from({ length: passengers.infantsOnLapCount }, () => ({
        age: 0,
        inSeat: false,
      })),
    ];

    const travelers = {
      adultsCount: passengers.adultsCount,
      children: passengers.childrenAges,
      infants,
    };

    // Seed Search State
    dispatch(setOrigin(tripTerminus));
    dispatch(setSearchedOriginResult(tripTerminus));
    dispatch(setDestination(destinationResult));
    dispatch(setSearchedDestinationResult(destinationResult));
    dispatch(setSearchedDates(fromDate, untilDate));
    dispatch(setDateRange({ from: departureDate, until: returnDate }));
    dispatch(setDepartureDate(fromDate));
    dispatch(setReturnDate(untilDate));
    dispatch(setTravelers(travelers));
    dispatch(setSearchedTravelers(travelers));

    // Seed Hotels State
    dispatch(selectLodging(selectedLodging.lodging as Lodging));
    dispatch(
      selectPackageByLodgingId(selectedLodging.package as PackagedLodging),
    );
    dispatch(
      setPackageHotelShopResults({
        hotelShopCallState: PackageHotelShopCallState.Success,
        payload: {
          packagesByRateId: hotelShopData.packagesByRateId as Record<
            string,
            PackagedRoom
          >,
          lodgingShopResponse: {
            type: PackageShopEnum.Rooms,
            value: hotelShopData.lodgingShopResponse,
          },
        },
      }),
    );
    dispatch(
      selectRoomType(
        selectedRoom.roomInfoIndex,
        selectedRoom.productIndex,
        selectedRoom.rateId,
      ),
    );

    // Seed Flights State
    dispatch(
      setPackagesFlightShopResults({
        flightShopCallState: CallState.Success,
        flightShopResults: {
          packagesByOutboundFareSlice:
            flightShopData.packagesByOutboundFareSlice as Record<
              string,
              PackagedOutboundFareSlice
            >,
          airShopSummary: {
            type: PackageShopEnum.Flights,
            value: flightShopData.flightShopResponse,
          },
          benchmarkSliceIds: flightShopData.benchmarkSliceIds,
        },
      }),
    );
    dispatch(
      setPackagesTripDetails({
        packagesByFareId: outboundTripDetailsData.packagesByFareId as Record<
          string,
          SafePackageDetails
        >,
        tripDetailsResponse: {
          type: PackageFetchDetailsEnum.Flight,
          value: outboundTripDetailsData.tripDetails,
        },
      }),
    );
    dispatch(
      setPackagesTripDetails({
        packagesByFareId: returnTripDetailsData.packagesByFareId as Record<
          string,
          SafePackageDetails
        >,
        tripDetailsResponse: {
          type: PackageFetchDetailsEnum.Flight,
          value: returnTripDetailsData.tripDetails,
        },
      }),
    );
    dispatch(
      setPackagesChosenOutgoingSlice(
        selectedOutboundFlight.sliceId,
        selectedOutboundFlight.fareId,
        selectedOutboundFlight.fareSliceId,
        selectedOutboundFlight.tripId,
      ),
    );
    dispatch(
      setPackagesChosenReturnSlice(
        selectedReturnFlight.tripId,
        selectedReturnFlight.sliceId,
        selectedReturnFlight.fareId,
        selectedReturnFlight.fareSliceId,
      ),
    );

    // Set Synced Flag
    setSynced(true);
  }, []);

  return synced;
};
